import { useMemo } from 'react';

import { TelemetryTypes } from '@store/enums';
import { EnergyFlowScenarioState } from '@store/types';
import {
    useHasGcp,
    useHasBattery,
    useHasInverter,
    useHasWallbox,
    useHasSmartMeter,
    useTelemetries,
    useHasHeatPump,
    useHasElectricCar,
    useIsGridXCustomer,
    useHasEmobility,
} from '@store/selectors';

export const useEnergyFlowScenarios = (
    isLive: boolean,
): EnergyFlowScenarioState => {
    const hasGCP = useHasGcp();
    const hasBattery = useHasBattery();
    const hasWallbox = useHasWallbox();
    const hasElectricCar = useHasElectricCar();
    const hasLiveData = useTelemetries(TelemetryTypes.PV_BATTERY).hasLiveData;
    const hasGCPLiveData = useTelemetries(TelemetryTypes.GCP).hasLiveData;
    const hasInverter = useHasInverter();
    const hasSmartMeter = useHasSmartMeter();
    const hasEmobility = useHasEmobility();
    const hasHeatPump = useHasHeatPump();
    const isGridXCustomer = useIsGridXCustomer();

    const isPvWithSmartMeter = useMemo(
        () => hasSmartMeter && hasInverter,
        [hasInverter, hasSmartMeter],
    );

    const smartMeterOnly = useMemo(
        () => !isPvWithSmartMeter && !hasInverter && hasGCP,
        [hasGCP, hasInverter, isPvWithSmartMeter],
    );

    const isPvWithSmarthome = useMemo(
        () => hasInverter && !hasGCP && (hasEmobility || hasHeatPump),
        [hasGCP, hasInverter, hasEmobility, hasHeatPump],
    );

    const isSmartMeterOnly = useMemo(
        () => smartMeterOnly && !hasEmobility && !hasHeatPump,
        [hasEmobility, smartMeterOnly, hasHeatPump],
    );

    const isSmartMeterAndSmartHome = useMemo(
        () => smartMeterOnly && (hasEmobility || hasHeatPump),
        [hasEmobility, smartMeterOnly, hasHeatPump],
    );

    const hasNoDevices = useMemo(
        () =>
            !hasInverter &&
            !hasGCP &&
            !hasWallbox &&
            !hasElectricCar &&
            !hasHeatPump,
        [hasGCP, hasInverter, hasWallbox, hasHeatPump, hasElectricCar],
    );

    const isPVonly = useMemo(
        () => !hasBattery && !hasGCP && !hasEmobility && !hasHeatPump,
        [hasGCP, hasEmobility, hasBattery, hasHeatPump],
    );

    const isSmartHomeOnly = useMemo(
        () => !hasInverter && !hasGCP && !hasBattery && hasEmobility,
        [hasGCP, hasEmobility, hasBattery, hasInverter],
    );

    const hasNoLiveData = useMemo(
        () =>
            isLive &&
            ((hasInverter && !hasLiveData) ||
                (isGridXCustomer && hasGCP && !hasGCPLiveData)),
        [
            hasInverter,
            hasLiveData,
            isLive,
            hasGCP,
            isGridXCustomer,
            hasGCPLiveData,
        ],
    );

    return {
        isPVonly,
        hasWallbox,
        hasElectricCar,
        hasInverter,
        hasNoDevices,
        hasSmartMeter,
        hasNoLiveData,
        smartMeterOnly,
        hasEmobility,
        isSmartHomeOnly,
        isSmartMeterOnly,
        isPvWithSmarthome,
        isPvWithSmartMeter,
        isSmartMeterAndSmartHome,
        hasHeatPump,
    };
};
